import { type TicketType } from '@cheqroom/api';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchPythonEndpoint, queryClient } from 'react-query-config';

import { useAuthOrThrow } from '../../context/Authentication.context';

export type TicketTypesResponse = TicketType[];

export type PythonTicketTypesResponse = {
	ticketTypes: TicketType[];
};

export const getTicketTypesQueryKey = (groupId: string) => {
	return ['ticketTypes', groupId];
};

export const useInvalidateTicketTypesQuery = () => {
	const authUser = useAuthOrThrow();
	const groupId = authUser.workspace.id;
	const queryKey = getTicketTypesQueryKey(groupId);
	const invalidateTicketTypesQuery = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: ['ticketTypes', groupId] });
	}, [groupId]);
	return {
		queryKey,
		invalidateTicketTypesQuery,
	} as const;
};

export interface PythonTicketTypesVariables {
	_fields?: string;
}

export const useTicketTypes = <TData = TicketTypesResponse, TError = unknown>(
	options?: UseQueryOptions<TicketTypesResponse, TError, TData>
) => {
	const authUser = useAuthOrThrow();
	const groupId = authUser.workspace.id;

	return useQuery<TicketTypesResponse, TError, TData>(
		getTicketTypesQueryKey(groupId),
		async () => {
			const fetchFn = fetchPythonEndpoint<PythonTicketTypesResponse, PythonTicketTypesVariables>(
				`${authUser.id}/null/jwt/groups/${groupId}`,
				{
					_fields: 'ticketTypes',
				},
				'GET'
			);
			const response = await fetchFn();
			return response.ticketTypes;
		},
		{
			keepPreviousData: true,
			staleTime: 5 * 60 * 1000, // 5 minutes
			...options,
		}
	);
};

export const isAdminUser = (): boolean => {
	return window.global.permissionHandler?.hasAdminPermission() ?? false;
};

export const getUserRoleId = (): string => {
	const user = window.global.central?.user();
	return user?.userRole?._id ?? user?.role ?? '';
};

export const hasTicketReadPermission = (): boolean => {
	return window.global.permissionHandler?.hasTicketPermission('read') ?? false;
};

export const filterTicketTypesByRole = (ticketTypes: TicketType[]): TicketType[] => {
	if (isAdminUser()) return ticketTypes;

	const userRoleId = getUserRoleId();
	const canReadTickets = hasTicketReadPermission();

	return ticketTypes.filter((tt) => {
		const isExplicitlyGranted =
			(tt.settings?.visibility_by_roles ?? []).length > 0
				? tt.settings?.visibility_by_roles?.includes(userRoleId)
				: true;
		return canReadTickets && isExplicitlyGranted;
	});
};

export const useTicketTypesForLocation = (locationId?: string) => {
	const select = useCallback(
		(data: TicketTypesResponse) =>
			filterTicketTypesByRole(
				data?.filter(
					(ticketType) =>
						!locationId ||
						!ticketType.settings?.available_locations?.length ||
						ticketType.settings.available_locations.includes(locationId)
				) ?? []
			),
		[locationId]
	);
	return useTicketTypes({ select });
};
